import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { useConfirm } from '../../context/ConfirmContext';
import { taskLinksAPI } from '../../services/taskServices';
import { tasksAPI } from '../../services/api';
import { TaskLink, LINK_TYPES } from '../../types/task';

interface TaskLinksProps {
  taskId: number;
  onUpdate?: () => void;
}

const TaskLinks: React.FC<TaskLinksProps> = ({ taskId, onUpdate }) => {
  const { t } = useTranslation();
  const { confirm } = useConfirm();
  const [outgoingLinks, setOutgoingLinks] = useState<TaskLink[]>([]);
  const [incomingLinks, setIncomingLinks] = useState<TaskLink[]>([]);
  const [loading, setLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<any[]>([]);
  const [selectedLinkType, setSelectedLinkType] = useState('relates_to');
  const [searching, setSearching] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchLinks();
  }, [taskId]);

  const fetchLinks = async () => {
    try {
      setLoading(true);
      const response = await taskLinksAPI.getAll(taskId);
      setOutgoingLinks(response.data.outgoing_links);
      setIncomingLinks(response.data.incoming_links);
    } catch (err) {
      console.error('Failed to fetch links:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = async (query: string) => {
    setSearchQuery(query);
    if (query.length < 2) {
      setSearchResults([]);
      return;
    }

    setSearching(true);
    try {
      const response = await tasksAPI.getAll();
      const tasks = response.data.tasks || [];
      const filtered = tasks
        .filter((t: any) => t.id !== taskId)
        .filter((t: any) =>
          t.title.toLowerCase().includes(query.toLowerCase()) ||
          `TASK-${t.id}`.toLowerCase().includes(query.toLowerCase())
        )
        .slice(0, 10);
      setSearchResults(filtered);
    } catch (err) {
      console.error('Failed to search tasks:', err);
    } finally {
      setSearching(false);
    }
  };

  const handleAddLink = async (targetTaskId: number) => {
    setSubmitting(true);
    try {
      await taskLinksAPI.create(taskId, targetTaskId, selectedLinkType);
      setShowAddForm(false);
      setSearchQuery('');
      setSearchResults([]);
      fetchLinks();
      onUpdate?.();
    } catch (err) {
      console.error('Failed to create link:', err);
    } finally {
      setSubmitting(false);
    }
  };

  const handleRemoveLink = async (linkId: number) => {
    const confirmed = await confirm({
      title: t('tasks.removeLinkTitle'),
      message: t('tasks.removeLinkConfirm'),
      confirmLabel: t('common.remove'),
      cancelLabel: t('common.cancel'),
      variant: 'warning',
    });
    if (!confirmed) return;

    try {
      await taskLinksAPI.delete(taskId, linkId);
      fetchLinks();
      onUpdate?.();
    } catch (err) {
      console.error('Failed to remove link:', err);
    }
  };

  const getLinkTypeLabel = (linkType: string, direction: 'outgoing' | 'incoming') => {
    const type = LINK_TYPES.find((t) => t.value === linkType);
    if (!type) return linkType;
    return direction === 'outgoing' ? type.outwardLabel : type.inwardLabel;
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'todo': return 'bg-gray-100 text-gray-600';
      case 'in_progress': return 'bg-blue-100 text-blue-600';
      case 'completed': return 'bg-green-100 text-green-600';
      case 'blocked': return 'bg-red-100 text-red-600';
      default: return 'bg-gray-100 text-gray-600';
    }
  };

  const renderLink = (link: TaskLink) => (
    <div key={link.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
      <div className="flex items-center gap-3">
        <span className="text-xs text-gray-500 capitalize">
          {getLinkTypeLabel(link.link_type, link.direction)}
        </span>
        <Link
          to={`/manager/tasks/${link.related_task.id}`}
          className="flex items-center gap-2 hover:text-blue-600"
        >
          <span className="text-sm font-medium text-gray-700">TASK-{link.related_task.id}</span>
          <span className="text-sm text-gray-600">{link.related_task.title}</span>
        </Link>
        <span className={`px-2 py-0.5 text-xs rounded ${getStatusColor(link.related_task.status)}`}>
          {link.related_task.status}
        </span>
      </div>
      <button
        onClick={() => handleRemoveLink(link.id)}
        className="p-1 text-gray-400 hover:text-red-500 transition-colors"
        title={t('tasks.removeLink')}
      >
        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
        </svg>
      </button>
    </div>
  );

  if (loading) {
    return (
      <div className="animate-pulse space-y-3">
        {[1, 2].map((i) => (
          <div key={i} className="h-12 bg-gray-200 rounded-lg" />
        ))}
      </div>
    );
  }

  const allLinks = [...outgoingLinks, ...incomingLinks];

  return (
    <div className="space-y-4">
      {/* Add link button/form */}
      {showAddForm ? (
        <div className="p-4 border border-gray-200 rounded-lg space-y-4">
          <div className="flex items-center gap-4">
            <select
              value={selectedLinkType}
              onChange={(e) => setSelectedLinkType(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
            >
              {LINK_TYPES.map((type) => (
                <option key={type.value} value={type.value}>
                  {type.label}
                </option>
              ))}
            </select>
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => handleSearch(e.target.value)}
              placeholder={t('tasks.searchTasks')}
              className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
              autoFocus
            />
            <button
              onClick={() => setShowAddForm(false)}
              className="p-2 text-gray-500 hover:text-gray-700"
            >
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Search results */}
          {searchResults.length > 0 && (
            <div className="border border-gray-200 rounded-lg divide-y max-h-60 overflow-y-auto">
              {searchResults.map((task) => (
                <button
                  key={task.id}
                  onClick={() => handleAddLink(task.id)}
                  disabled={submitting}
                  className="w-full flex items-center gap-3 p-3 hover:bg-gray-50 text-left disabled:opacity-50"
                >
                  <span className="text-sm font-medium text-gray-600">TASK-{task.id}</span>
                  <span className="text-sm text-gray-700 flex-1 truncate">{task.title}</span>
                  <span className={`px-2 py-0.5 text-xs rounded ${getStatusColor(task.status)}`}>
                    {task.status}
                  </span>
                </button>
              ))}
            </div>
          )}

          {searching && (
            <div className="text-center py-4 text-gray-500">{t('common.searching')}</div>
          )}

          {searchQuery.length >= 2 && !searching && searchResults.length === 0 && (
            <div className="text-center py-4 text-gray-500">{t('tasks.noTasksFound')}</div>
          )}
        </div>
      ) : (
        <button
          onClick={() => setShowAddForm(true)}
          className="flex items-center gap-2 text-sm text-blue-600 hover:text-blue-700"
        >
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
          {t('tasks.linkIssue')}
        </button>
      )}

      {/* Links list */}
      {allLinks.length === 0 ? (
        <p className="text-center text-gray-500 py-8">{t('tasks.noLinks')}</p>
      ) : (
        <div className="space-y-2">
          {allLinks.map((link) => renderLink(link))}
        </div>
      )}
    </div>
  );
};

export default TaskLinks;
